import { createContext, useCallback, useContext, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { MaterialIcon, TeachModal } from '@lapo/ui';
import type { ProducerId } from '@lapo/core';

/**
 * HONE — the global "Teach" affordance. One provider mounted at the app root owns a single {@link TeachModal};
 * anywhere in the tree can call `openTeach()` (with an optional context) to pop it, pre-scoped to a card or
 * the active view. This is the app's one deliberately-tiny shared-state primitive — a React context over
 * `useState`, so the card stays prop-drilled and no state-management framework is forced on the codebase.
 */
export interface TeachTarget {
  /** A note about a specific signal routes through the uniform capture (KIND_PRODUCER). */
  signalId?: string;
  /** A note from a view is producer-addressed (e.g. `ledger` for #/decisions). */
  producerId?: ProducerId;
  entityRef?: string;
  /** A short human line shown in the modal title (the card title, or the view name). */
  label?: string;
}

interface TeachContextValue {
  openTeach: (target?: TeachTarget) => void;
}

const TeachCtx = createContext<TeachContextValue | null>(null);

/** Open the global Teach modal from anywhere in the tree. */
export function useTeach(): TeachContextValue {
  return useContext(TeachCtx) ?? { openTeach: () => {} };
}

export interface TeachProviderProps {
  /** Records the note; returns true on success. Wired to `apiClient.teach`. */
  onTeach: (input: { note: string; signalId?: string; producerId?: ProducerId; entityRef?: string }) => Promise<boolean>;
  children: ReactNode;
}

export function TeachProvider({ onTeach, children }: TeachProviderProps): ReactElement {
  const [target, setTarget] = useState<TeachTarget | null>(null);
  const openTeach = useCallback((t?: TeachTarget) => setTarget(t ?? {}), []);
  const value = useMemo(() => ({ openTeach }), [openTeach]);

  return (
    <TeachCtx.Provider value={value}>
      {children}
      <TeachModal
        open={target != null}
        contextLabel={target?.label}
        onSubmit={async (note) => {
          await onTeach({ note, signalId: target?.signalId, producerId: target?.producerId, entityRef: target?.entityRef });
        }}
        onClose={() => setTarget(null)}
      />
    </TeachCtx.Provider>
  );
}

/**
 * The always-available chrome "Teach" affordance. Rendered inside {@link TeachProvider}, it opens the
 * modal pre-scoped to the current view's producer — so the owner can teach Lapo from anywhere, even on a
 * surface with no signal to attach to.
 */
export function GlobalTeachButton({ producerId, label }: { producerId: ProducerId; label?: string }): ReactElement {
  const { openTeach } = useTeach();
  return (
    <button
      type="button"
      className="lapo-teach-fab"
      aria-label="Teach Lapo"
      title="Teach Lapo"
      data-testid="global-teach"
      onClick={() => openTeach({ producerId, label })}
    >
      <MaterialIcon name="school" size={16} /> Teach
    </button>
  );
}
